import type { DayPrediction, TimeSlot, AltDay, SlotDataSource, CrowdLevel } from '../types';
import type { SogralDeparture, SogralApiDeparture } from '../types/sogral';
import { fetchDepartures, fetchDeparturesJson, formatDateForSogral, getScheduledTime } from './sogralApi';
import { wilayaFromP1 } from './liveSearchService';
import {
  scoreToLevel,
  reconstructSchedule,
  aggregateByTime,
  buildPredictedSlots,
  type TimedOccurrence,
} from './schedulePrediction';

// Re-export pour compat (dashboardService / transporterService l'importent d'ici)
export { scoreToLevel };

const HISTORY_WINDOW_DAYS = 21;
const MIN_SAMPLES_FOR_HISTORICAL = 3;

// Poids synthétiques par jour — fallback uniquement pour le score des jours alternatifs.
const DAY_WEIGHTS: Record<number, number> = {
  0: 1.4, 1: 0.7, 2: 0.6, 3: 0.65, 4: 0.8, 5: 1.6, 6: 1.3,
};

// ---------- Occurrences historiques (par horaire exact) ----------

async function getDayOfWeekOccurrences(
  agencyId: number,
  destinationId: string,
  dayOfWeek: number,
): Promise<TimedOccurrence[]> {
  const wilaya = wilayaFromP1(destinationId);
  if (wilaya === null) return [];

  const agencyIds = agencyId === 0 ? [1, 2] : [agencyId];
  const todayStr = new Date().toISOString().split('T')[0];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - HISTORY_WINDOW_DAYS);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  const all: SogralApiDeparture[] = [];
  for (const id of agencyIds) {
    try { all.push(...(await fetchDeparturesJson(id))); } catch { /* skip */ }
  }

  const out: TimedOccurrence[] = [];
  for (const d of all) {
    if (d.S7 !== wilaya) continue;
    if (d.P8 !== 2) continue;                            // seulement les bus assurés
    if (!d.P11 || typeof d.P12 !== 'number') continue;
    const depDate = d.S8?.split('T')[0];
    if (!depDate || depDate >= todayStr || depDate < cutoffStr) continue;  // passé, dans la fenêtre
    const dt = new Date(depDate + 'T00:00:00');
    if (dt.getDay() !== dayOfWeek) continue;
    const time = getScheduledTime(d);
    if (!time) continue;
    out.push({ time, date: depDate, seatsSold: d.P12, seatsTotal: d.P11 });
  }
  return out;
}

function overallFallbackScore(occurrences: TimedOccurrence[]): number {
  let sold = 0;
  let total = 0;
  for (const o of occurrences) {
    if (o.seatsTotal > 0) { sold += o.seatsSold; total += o.seatsTotal; }
  }
  return total > 0 ? Math.round((sold / total) * 100) : 50;
}

// ---------- Grille live du jour (par horaire exact) ----------

function buildLiveSlotsByTime(deps: SogralDeparture[]): TimeSlot[] {
  const byTime = new Map<string, { avail: number; total: number; count: number }>();
  for (const d of deps) {
    const e = byTime.get(d.time) ?? { avail: 0, total: 0, count: 0 };
    e.avail += d.seatsAvailable;
    e.total += d.seatsTotal;
    e.count += 1;
    byTime.set(d.time, e);
  }
  const slots: TimeSlot[] = [];
  for (const [time, e] of byTime) {
    const occ = e.total > 0 ? (e.total - e.avail) / e.total : 0;
    const score = Math.round(occ * 100);
    slots.push({
      time,
      crowd_level: scoreToLevel(score),
      crowd_score: score,
      is_recommended: false,
      data_source: 'live',
      sample_size: e.count,
      total_departures: e.count,
      low_confidence: false,
    });
  }
  return slots.sort((a, b) => a.time.localeCompare(b.time));
}

// ---------- Prédiction principale ----------

export async function predictDay(
  agencyId: number,
  destinationId: string,
  date: string,
): Promise<DayPrediction> {
  const target = new Date(date + 'T00:00:00');
  const dayOfWeek = target.getDay();
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = date === todayStr;

  let slots: TimeSlot[] = [];

  // Aujourd'hui : grille = vrais départs live du jour.
  if (isToday) {
    const agencyIds = agencyId === 0 ? [1, 2] : [agencyId];
    const liveDeps: SogralDeparture[] = [];
    for (const id of agencyIds) {
      try {
        const deps = await fetchDepartures({
          agencyId: id, destinationId, date: formatDateForSogral(target), periode: 0,
        });
        liveDeps.push(...deps);
      } catch { /* skip */ }
    }
    slots = buildLiveSlotsByTime(liveDeps.filter((d) => d.status !== 'annule'));
  }

  // Sinon (ou si pas de live aujourd'hui) : planning historique récurrent + remplissage moyen par horaire.
  if (slots.length === 0) {
    const occurrences = await getDayOfWeekOccurrences(agencyId, destinationId, dayOfWeek);
    const schedule = reconstructSchedule(occurrences);
    const agg = aggregateByTime(occurrences);
    slots = buildPredictedSlots(schedule, agg, overallFallbackScore(occurrences));
  }

  // Recommandation : créneaux à <= min+10, sur les vrais horaires uniquement.
  if (slots.length > 0) {
    const minScore = Math.min(...slots.map((s) => s.crowd_score));
    for (const slot of slots) slot.is_recommended = slot.crowd_score <= minScore + 10;
  }

  const overallScore = slots.length
    ? Math.round(slots.reduce((s, sl) => s + sl.crowd_score, 0) / slots.length)
    : 0;

  return {
    date,
    day_label: getDayLabel(dayOfWeek),
    overall_level: scoreToLevel(overallScore),
    slots,
    no_data: slots.length === 0,
    alt_days: await generateAltDays(agencyId, destinationId, date),
  };
}

// ---------- Jours alternatifs (logique inchangée) ----------

async function generateAltDays(
  agencyId: number,
  destinationId: string,
  fromDate: string,
): Promise<AltDay[]> {
  const wilaya = wilayaFromP1(destinationId);
  if (wilaya === null) return [];

  const agencyIds = agencyId === 0 ? [1, 2] : [agencyId];
  const todayStr = new Date().toISOString().split('T')[0];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - HISTORY_WINDOW_DAYS);
  const cutoffStr = cutoff.toISOString().split('T')[0];
  const all: SogralApiDeparture[] = [];
  for (const id of agencyIds) {
    try {
      all.push(...(await fetchDeparturesJson(id)));
    } catch { /* skip */ }
  }

  const byDow = new Map<number, { totalSold: number; totalSeats: number; count: number }>();
  for (let i = 0; i < 7; i++) byDow.set(i, { totalSold: 0, totalSeats: 0, count: 0 });

  for (const d of all) {
    if (d.S7 !== wilaya || d.P8 !== 2) continue;
    if (!d.P11 || typeof d.P12 !== 'number') continue;
    const depDate = d.S8?.split('T')[0];
    if (!depDate || depDate >= todayStr || depDate < cutoffStr) continue;
    const dt = new Date(depDate + 'T00:00:00');
    const agg = byDow.get(dt.getDay())!;
    agg.totalSold += d.P12;
    agg.totalSeats += d.P11;
    agg.count += 1;
  }

  const dowScore = new Map<number, number>();
  for (const [dow, agg] of byDow) {
    if (agg.count < MIN_SAMPLES_FOR_HISTORICAL || agg.totalSeats === 0) {
      dowScore.set(dow, (DAY_WEIGHTS[dow] ?? 1.0) * 50);
    } else {
      dowScore.set(dow, Math.round((agg.totalSold / agg.totalSeats) * 100));
    }
  }

  const target = new Date(fromDate + 'T00:00:00');
  const targetScore = dowScore.get(target.getDay()) ?? 50;
  const candidates: AltDay[] = [];

  for (let i = 1; i <= 7 && candidates.length < 3; i++) {
    const cand = new Date(target);
    cand.setDate(target.getDate() + i);
    const dow = cand.getDay();
    const score = dowScore.get(dow) ?? 50;
    if (score < targetScore - 5) {
      candidates.push({
        date: cand.toISOString().split('T')[0],
        label: `${getDayShort(dow)} ${cand.getDate()}`,
        level: scoreToLevel(score),
      });
    }
  }

  return candidates;
}

function getDayLabel(dow: number): string {
  return ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'][dow];
}

function getDayShort(dow: number): string {
  return ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dow];
}

// ---------- Détail des départs (utilisé par SearchButton) ----------

export interface DepartureDetail {
  time: string;
  destination: string;
  transporterName: string;
  seatsAvailable: number;
  seatsTotal: number;
  occupancyPercent: number;
  crowdLevel: CrowdLevel;
  status: string;
}

export function departuresToDetails(departures: SogralDeparture[]): DepartureDetail[] {
  return departures
    .filter((d) => d.status !== 'annule')
    .map((d) => ({
      time: d.time,
      destination: d.destination,
      transporterName: d.transporterName,
      seatsAvailable: d.seatsAvailable,
      seatsTotal: d.seatsTotal,
      occupancyPercent: Math.round(d.occupancyRate * 100),
      crowdLevel: scoreToLevel(Math.round(d.occupancyRate * 100)),
      status: d.status === 'assure' ? 'Assuré' : 'Ouvert',
    }));
}

// Re-export pour compat
export type { SlotDataSource };
